import { createSlice } from "@reduxjs/toolkit";
import {
  fetchQboConnectionStatus,
  startQboOAuth,
  disconnectQbo,
  fetchQboInvoices,
  updateQboSyncSettings,
  syncPendingInvoices,
  syncPendingPayments,
  testQboConnection,
} from "../thunks/qboThunk";

const qboSlice = createSlice({
  name: "qbo",
  initialState: {
    isConnected: null, // null = loading, true = connected, false = disconnected
    status: "idle",
    error: null,
    realmId: null,
    companyName: null,
    syncSettings: {
      autoSyncInvoices: false,
      autoSyncPayments: false,
      autoSyncCustomers: false,
      syncFrequency: 'daily',
      notificationEmail: ''
    },
    invoices: [], // To store fetched invoices
    pagination: {
      totalInvoices: 0,
      currentPage: 1,
      totalPages: 1,
      pageSize: 10,
    },
  },
  reducers: {
    setQboSyncSettings: (state, action) => {
      state.syncSettings = { ...state.syncSettings, ...action.payload };
    },
    clearQboData: (state) => {
      state.isConnected = null;
      state.realmId = null;
      state.companyName = null;
      state.syncSettings = {
        autoSyncInvoices: false,
        autoSyncPayments: false,
        autoSyncCustomers: false,
        syncFrequency: 'daily',
        notificationEmail: ''
      };
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch QuickBooks Connection Status
      .addCase(fetchQboConnectionStatus.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchQboConnectionStatus.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.isConnected = action.payload.connected;
        if (action.payload.connected) {
          state.realmId = action.payload.realmId;
          state.companyName = action.payload.companyName;
          state.syncSettings = { ...state.syncSettings, ...action.payload.syncSettings };
        }
      })
      .addCase(fetchQboConnectionStatus.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // Start QuickBooks OAuth
      .addCase(startQboOAuth.pending, (state) => {
        state.status = "loading";
      })
      .addCase(startQboOAuth.fulfilled, (state) => {
        state.status = "succeeded";
        // No state changes needed, OAuth flow happens via redirect
      })
      .addCase(startQboOAuth.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // Disconnect QuickBooks
      .addCase(disconnectQbo.pending, (state) => {
        state.status = "loading";
      })
      .addCase(disconnectQbo.fulfilled, (state) => {
        state.status = "succeeded";
        state.isConnected = false;
        state.realmId = null;
        state.companyName = null;
        state.syncSettings = {
          autoSyncInvoices: false,
          autoSyncPayments: false,
          autoSyncCustomers: false,
          syncFrequency: 'daily',
          notificationEmail: ''
        };
      })
      .addCase(disconnectQbo.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // Fetch QuickBooks Invoices
      .addCase(fetchQboInvoices.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchQboInvoices.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.invoices = action.payload.invoices;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchQboInvoices.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // Update QuickBooks Sync Settings
      .addCase(updateQboSyncSettings.pending, (state) => {
        state.status = "loading";
      })
      .addCase(updateQboSyncSettings.fulfilled, (state, action) => {
        state.status = "succeeded";
        if (action.payload.syncSettings) {
          state.syncSettings = { ...state.syncSettings, ...action.payload.syncSettings };
        }
      })
      .addCase(updateQboSyncSettings.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // Sync Pending Invoices
      .addCase(syncPendingInvoices.pending, (state) => {
        state.status = "loading";
      })
      .addCase(syncPendingInvoices.fulfilled, (state, action) => {
        state.status = "succeeded";
      })
      .addCase(syncPendingInvoices.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // Sync Pending Payments
      .addCase(syncPendingPayments.pending, (state) => {
        state.status = "loading";
      })
      .addCase(syncPendingPayments.fulfilled, (state, action) => {
        state.status = "succeeded";
      })
      .addCase(syncPendingPayments.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // Test QuickBooks Connection
      .addCase(testQboConnection.pending, (state) => {
        state.status = "loading";
      })
      .addCase(testQboConnection.fulfilled, (state, action) => {
        state.status = "succeeded";
      })
      .addCase(testQboConnection.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      });
  },
});

export const { setQboSyncSettings, clearQboData } = qboSlice.actions;
export default qboSlice.reducer;
